import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Building2, Car, CalendarDays, Users, Settings, Activity, LogOut, Bell, Menu, X, FileText, ShieldAlert, BedDouble, FolderCheck, FileSignature } from "lucide-react";

const NAV_ADMIN = [
  { to: "/admin", end: true, label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/admin/agencies", label: "Agences", icon: Building2, adminOnly: true },
  { to: "/admin/dossier", label: "Mon dossier", icon: FolderCheck, agencyOnly: true },
  { to: "/admin/contract", label: "Mon contrat", icon: FileSignature, agencyOnly: true },
  { to: "/admin/vehicles", label: "Véhicules", icon: Car },
  { to: "/admin/lodgings", label: "Logements", icon: BedDouble },
  { to: "/admin/bookings", label: "Réservations", icon: CalendarDays },
  { to: "/admin/users", label: "Utilisateurs", icon: Users, adminOnly: true },
  { to: "/admin/activity", label: "Journal d'activité", icon: Activity, adminOnly: true },
  { to: "/admin/login-logs", label: "Journal connexions", icon: ShieldAlert, adminOnly: true },
  { to: "/admin/legal", label: "Pages légales", icon: FileText, adminOnly: true },
  { to: "/admin/settings", label: "Paramètres", icon: Settings, adminOnly: true },
];

function SidebarContent({ onNav }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV_ADMIN.filter((n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (n.agencyOnly) return user?.role === "agency_partner";
    return true;
  });
  return (
    <>
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <Logo />
        {onNav && (
          <button onClick={() => onNav()} className="lg:hidden h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center" data-testid="close-sidebar">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => onNav && onNav()}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-[#0a1e3f] text-white" : "text-gray-600 hover:bg-gray-50"
              }`
            }
          >
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#0a1e3f] text-[#d4a437] font-bold flex items-center justify-center">{user?.full_name?.[0]}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#0a1e3f] truncate">{user?.full_name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.role === "admin" ? "Administrateur" : "Agence partenaire"}</div>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="mt-3 w-full text-xs text-red-600 font-semibold flex items-center justify-center gap-2 py-2 hover:bg-red-50 rounded-lg">
          <LogOut className="h-3.5 w-3.5" /> Déconnexion
        </button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#f8f9fa] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 fixed left-0 top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="lg:hidden fixed left-0 top-0 h-screen w-72 bg-white z-50 flex flex-col shadow-2xl">
            <SidebarContent onNav={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 lg:ml-64 min-w-0">
        <div className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center"
              data-testid="open-sidebar"
            >
              <Menu className="h-4 w-4 text-[#0a1e3f]" />
            </button>
            <div className="text-sm text-gray-500 hidden sm:block">Bonjour, <span className="font-semibold text-[#0a1e3f]">{user?.full_name}</span></div>
          </div>
          <div className="flex items-center gap-3">
            <button className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center relative"><Bell className="h-4 w-4 text-[#0a1e3f]" /></button>
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8"><Outlet /></div>
      </main>
    </div>
  );
}
